import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, InjectionToken, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogContent } from '@angular/material/dialog';
import { CBox_ActionCreateAgentPermittedActionEnum } from '@server/services/cbox/public/api/v1/resources/agent/types';
import { CBox_PublicSuccessResponse } from '@server/services/cbox/public/api/v1/resources/common/request_base/types';
import { CBox_GetLockerBaseData } from '@server/services/cbox/public/api/v1/resources/locker/types';
import { CBox_PublicGetAgentActivityDataListItem, CBox_PublicGetAgentActivityDataResponse } from '@server/services/cbox/public/api/v1/resources/reports/types';
import { CBoxProfileDropOffRequestInfoComponent } from 'app/profile/requests/drop-off/dialogs/info/cbox-profile-dropoff-request-info.component';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { lockerCleanAddress } from 'src/helpers/functions';
import { ApiService, handlePublicApiError } from 'src/services/api/api.service';
import { ConfigurationService } from 'src/services/configuration/configuration.service';
import { ToastService } from 'src/services/toast/toast.service';

@Component({
  selector: 'app-cbox-profile-agent-session-info-dialog',
  templateUrl: './cbox-profile-agent-session-info-dialog.component.html',
  styleUrls: ["./cbox-profile-agent-session-info-dialog.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MatDialogContent,
    MatDialogClose,
    TableModule,
    ProgressSpinnerModule,
    AccordionModule,
    SkeletonModule
  ]
})

export class CBoxProfileAgentSessionInfoDialogComponent implements OnInit {

  loading = signal(false);
  sessionData = signal<CBox_PublicGetAgentActivityDataResponse | undefined>(undefined);

  displayedColumns = signal<{
    name: string
  }[]>([]);
  columnsFetched = signal(false);

  actions = CBox_ActionCreateAgentPermittedActionEnum;

  constructor(
    private api: ApiService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private configurationService: ConfigurationService,
    @Inject(MAT_DIALOG_DATA) private procedureId: string,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>) {}

  ngOnInit(): void {
    this.init();
  }

  private async init(): Promise<void> {
    this.fetchSessionInfo();
    const columns = ((await this.configurationService.getConfigKey("page.pickUp.layout" as any)) as any)?.table?.find((table: any) => table.name === "main")?.columns;
    this.displayedColumns.set(columns);
    this.columnsFetched.set(true);
  }

  getActionParcelCount(
    actionType:
        CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]["type"]):
    number {
    return this.sessionData()?.actions.counts.find((a: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]) => a.type === actionType)?.count ?? 0;
  }

  getFilteredActions(actionType: CBox_ActionCreateAgentPermittedActionEnum): CBox_PublicGetAgentActivityDataResponse["actions"]["details"] {
    return this.sessionData()?.actions.details.filter((a) => a.type === actionType) ?? [];
  }

  openOrderInfo(action: CBox_PublicGetAgentActivityDataResponse["actions"]["details"][0]): void {
    switch (action.type) {
      case CBox_ActionCreateAgentPermittedActionEnum.COURIER_PUT_ORDERS: {
        this.openPickUpRequestInfo(action.request);
        break;
      }

      case CBox_ActionCreateAgentPermittedActionEnum.COURIER_TAKE_ORDERS: {
        this.openDropOffRequestInfo(action.request);
        break;
      }

      case CBox_ActionCreateAgentPermittedActionEnum.COURIER_RETRIEVE_EXPIRED: {
        this.openPickUpRequestInfo(action.request);
      }
    }
  }

  openPickUpRequestInfo(request: CBox_PublicGetAgentActivityDataResponse["actions"]["details"][0]["request"]): void {
    this.dialog.open(CBoxProfileDropOffRequestInfoComponent, {
      data: {
        responseId: request.responseId,
        awb: request.awb
      },
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });
  }

  openDropOffRequestInfo(request: CBox_PublicGetAgentActivityDataResponse["actions"]["details"][0]["request"]): void {
    this.dialog.open(CBoxProfileDropOffRequestInfoComponent, {
      data: {
        responseId: request.responseId,
        awb: request.awb
      },
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });
  }

  formatLockerAddress(address: CBox_GetLockerBaseData["addressParts"] | undefined): string {
    if (!address) {
      return "";
    }
    return lockerCleanAddress(address);
  }

  private fetchSessionInfo(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loading.set(true);
      this.api.get<CBox_PublicSuccessResponse<CBox_PublicGetAgentActivityDataResponse>>("backend/reports/agents/activity/" + this.procedureId).subscribe((session) => {
        this.sessionData.set(session.data);
        this.loading.set(false);
      }, (e: HttpErrorResponse) => {
        this.loading.set(false);
        handlePublicApiError(e, this.toastService);
      });
    }
  }

  private openOrderDetails(): void {

  }
}